// Wordle game logic — pure functions, no React. Imported by both the
// WordlePage UI and any future test harness or scripts.

import ANSWERS from './answers'

// Local-timezone YYYY-MM-DD key. Both both users live in EEST so
// the calendar day matches anyway, but doing it locally (rather than
// UTC midnight) means the daily word doesn't rotate at 3 a.m. for
// either of them.
export function localDateKey(date = new Date()) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// Stable hash of the date key. djb2 — deterministic, distributes
// consecutive days across the alphabet so today's "abate" isn't sat
// next to tomorrow's "abbey". `>>> 0` keeps it unsigned 32-bit.
function hashDateKey(key) {
  let h = 5381
  for (let i = 0; i < key.length; i++) {
    h = ((h << 5) + h + key.charCodeAt(i)) >>> 0
  }
  return h
}

// Daily answer for a given date. Same input → same output everywhere.
export function pickDailyWord(date = new Date()) {
  const idx = hashDateKey(localDateKey(date)) % ANSWERS.length
  return ANSWERS[idx]
}

// Practice mode word — uniform random. Not persisted, doesn't count
// for streaks. Lets you fool around without burning your daily.
export function pickPracticeWord() {
  return ANSWERS[Math.floor(Math.random() * ANSWERS.length)]
}

// Validate a typed guess. The answer pool is the only valid-guesses
// list we ship in PR 1, so "AALII"-style obscure-but-real guesses
// won't pass — see the header in answers.js for the trade-off.
export function isValidGuess(guess) {
  return typeof guess === 'string'
    && guess.length === 5
    && ANSWERS.includes(guess.toLowerCase())
}

// Per-letter scoring. Handles duplicate letters the way Wordle does:
//   1. Mark all exact-position matches first ("correct").
//   2. Then mark in-the-word-but-wrong-position ("present"), but only
//      as many times as that letter actually appears in the answer
//      minus the already-marked correct positions. Without the two
//      passes a guess like "ALLEY" against "AROSE" would mark both
//      Ls present even though there's only one L in the answer.
// Returns: ['correct' | 'present' | 'absent', ...] of length 5.
export function scoreGuess(guess, answer) {
  const result = Array(5).fill('absent')
  const remaining = answer.split('')
  for (let i = 0; i < 5; i++) {
    if (guess[i] === answer[i]) {
      result[i] = 'correct'
      remaining[i] = null
    }
  }
  for (let i = 0; i < 5; i++) {
    if (result[i] === 'correct') continue
    const idx = remaining.indexOf(guess[i])
    if (idx !== -1) {
      result[i] = 'present'
      remaining[idx] = null
    }
  }
  return result
}

// Aggregate per-letter state for keyboard colouring. Best-seen wins:
// correct > present > absent — so once you've placed a letter green,
// a later guess where the same letter ends up yellow doesn't downgrade.
export function computeKeyboardState(guesses, answer) {
  const rank = { correct: 3, present: 2, absent: 1 }
  const state = {}
  for (const g of guesses) {
    const scored = scoreGuess(g, answer)
    for (let i = 0; i < 5; i++) {
      const letter = g[i].toLowerCase()
      const next = scored[i]
      if (!state[letter] || rank[next] > rank[state[letter]]) {
        state[letter] = next
      }
    }
  }
  return state
}

// Emoji grid the Share button copies. The blank line above the grid
// is the canonical NYT layout — "Phoenix Wordle 2026-05-29 4/6\n\n🟩…".
export function buildEmojiGrid(guesses, answer) {
  return guesses
    .map((g) =>
      scoreGuess(g, answer)
        .map((s) => (s === 'correct' ? '🟩' : s === 'present' ? '🟨' : '⬛'))
        .join(''),
    )
    .join('\n')
}

// Walk back from today through completed rows to count consecutive
// winning days. Today's row only contributes once it's a confirmed win
// — if you haven't finished today yet, the streak you see is the one
// you carried in this morning, and it ticks up the moment you solve it.
// `rows`: array of { day: 'YYYY-MM-DD', won: boolean|null } in any order.
export function computeStreak(rows, today = new Date()) {
  if (!rows?.length) return 0
  const byDay = new Map()
  for (const r of rows) byDay.set(r.day, r)
  let streak = 0
  // Start at today if it's already a win, else yesterday. Don't break
  // on "today not played yet" — that's a normal mid-day state.
  const cursor = new Date(today)
  const todayRow = byDay.get(localDateKey(cursor))
  if (!todayRow || todayRow.won == null) cursor.setDate(cursor.getDate() - 1)
  while (true) {
    const row = byDay.get(localDateKey(cursor))
    if (!row || !row.won) break
    streak += 1
    cursor.setDate(cursor.getDate() - 1)
  }
  return streak
}
